/**
 * Client-local pinned-identity store (PKA L3, trust root).
 *
 * The identity public keys peers publish are served by the untrusted server, so
 * a signature alone only moves substitution to the identity key. We pin each
 * peer's identity key on first use (TOFU) and let the user mark it *verified*
 * after comparing a safety number out-of-band. On any later change of a pinned
 * key we refuse to silently trust the new one — the caller must block the
 * operation and force re-verification. This is what actually defeats a malicious
 * server for verified relationships.
 *
 * Stored per peer under `hbs:pinnedIdentity:<peerUserId>` in localStorage;
 * cleared on logout by clearAllLocalData (the `hbs:` prefix). Values are public
 * keys + a verified flag — no secrets — so plain localStorage is fine.
 */

export interface PinnedIdentity {
  identityPublicKey: string; // base64url(32) Ed25519
  verified: boolean;         // true once the user confirmed the safety number OOB
  pinnedAt: string;          // ISO-8601
}

function storageKey(peerUserId: string): string {
  return `hbs:pinnedIdentity:${peerUserId}`;
}

export function getPinnedIdentity(peerUserId: string): PinnedIdentity | null {
  try {
    const raw = localStorage.getItem(storageKey(peerUserId));
    return raw ? (JSON.parse(raw) as PinnedIdentity) : null;
  } catch {
    return null;
  }
}

function save(peerUserId: string, record: PinnedIdentity): void {
  try {
    localStorage.setItem(storageKey(peerUserId), JSON.stringify(record));
  } catch {
    // best-effort; a failed pin surfaces as "unverified" on the next read
  }
}

/**
 * Pin a peer's served identity key on first use (TOFU). If a different key is
 * already pinned, does NOT overwrite it and reports `changed: true` so the
 * caller can block and demand re-verification. `changed` is false for a fresh
 * pin and for a matching re-pin.
 */
export function pinIdentity(
  peerUserId: string,
  identityPublicKey: string,
): { record: PinnedIdentity; changed: boolean } {
  const existing = getPinnedIdentity(peerUserId);
  if (!existing) {
    const record: PinnedIdentity = { identityPublicKey, verified: false, pinnedAt: new Date().toISOString() };
    save(peerUserId, record);
    return { record, changed: false };
  }
  if (existing.identityPublicKey !== identityPublicKey) {
    return { record: existing, changed: true };
  }
  return { record: existing, changed: false };
}

/** Mark the pinned identity for a peer as verified (safety number confirmed). */
export function markIdentityVerified(peerUserId: string): void {
  const existing = getPinnedIdentity(peerUserId);
  if (existing) save(peerUserId, { ...existing, verified: true });
}

/** True when the peer's identity is pinned AND matches AND is verified. */
export function isIdentityVerified(peerUserId: string, identityPublicKey: string): boolean {
  const existing = getPinnedIdentity(peerUserId);
  return existing !== null && existing.identityPublicKey === identityPublicKey && existing.verified;
}
